import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { store } from '../store/store';

// ---------------------------------------------------------------------
// Permission -> Module mapping (single source of truth, no role names)
// ---------------------------------------------------------------------
export interface ModuleConfig {
  key: string;
  label: string;
  prefixes: string[];
  viewPermission: string;
  path?: string;
}

export const MODULE_CONFIGS: ModuleConfig[] = [
  { key: 'dashboard', label: 'Dashboard', prefixes: ['DASHBOARD_'], viewPermission: 'DASHBOARD_VIEW', path: '/tenant/dashboard' },
  { key: 'user', label: 'User Management', prefixes: ['USER_'], viewPermission: 'USER_VIEW', path: '/tenant/users' },
  { key: 'role', label: 'Role Management', prefixes: ['ROLE_', 'SCREEN_PERMISSION_'], viewPermission: 'ROLE_VIEW', path: '/tenant/roles' },
  { key: 'asset', label: 'Asset Management', prefixes: ['ASSET_'], viewPermission: 'ASSET_VIEW', path: '/tenant/assets' },
  { key: 'collection', label: 'Collection Management', prefixes: ['COLLECTION_'], viewPermission: 'COLLECTION_VIEW', path: '/tenant/collections' },
  { key: 'artwork', label: 'Artwork Management', prefixes: ['ARTWORK_'], viewPermission: 'ARTWORK_VIEW', path: '/tenant/artworks' },
  { key: 'campaign', label: 'Campaign Management', prefixes: ['CAMPAIGN_', 'LANDING_PAGE_', 'CAMPAIGN_PAGE_'], viewPermission: 'CAMPAIGN_VIEW', path: '/tenant/campaigns' },
  { key: 'product', label: 'Product Management', prefixes: ['PRODUCT_'], viewPermission: 'PRODUCT_VIEW', path: '/tenant/products' },
  { key: 'event', label: 'Event Management', prefixes: ['EVENT_'], viewPermission: 'EVENT_VIEW', path: '/tenant/events' },
  { key: 'content', label: 'Content Management', prefixes: ['CONTENT_'], viewPermission: 'CONTENT_VIEW', path: '/tenant/content' },
  { key: 'team', label: 'Team Management', prefixes: ['TEAM_'], viewPermission: 'TEAM_VIEW', path: '/tenant/teams' },
  { key: 'client', label: 'Client Management', prefixes: ['CLIENT_', 'SERVICE_', 'FINANCE_COORDINATE'], viewPermission: 'CLIENT_VIEW', path: '/tenant/clients' },
  { key: 'tenant', label: 'Tenant Management', prefixes: ['TENANT_'], viewPermission: 'TENANT_VIEW', path: '/super-admin/tenants' },
  { key: 'watchFolder', label: 'Watch Folder Management', prefixes: ['WATCH_FOLDER_'], viewPermission: 'WATCH_FOLDER_VIEW', path: '/tenant/watch-folders' },
  { key: 'guest', label: 'Guest Management', prefixes: ['GUEST_', 'SHOWCASE_'], viewPermission: 'GUEST_VIEW', path: '/tenant/guest-access' },
  { key: 'security', label: 'Security Management', prefixes: ['SECURITY_', 'ACCESS_MANAGE', 'SSO_CONFIGURE'], viewPermission: 'SECURITY_VIEW', path: '/tenant/security' },
  { key: 'settings', label: 'Settings Management', prefixes: ['SETTINGS_'], viewPermission: 'SETTINGS_VIEW', path: '/tenant/settings' },
  { key: 'billing', label: 'Billing Management', prefixes: ['BILLING_'], viewPermission: 'BILLING_VIEW', path: '/tenant/billing' },
  { key: 'cloud', label: 'Cloud Management', prefixes: ['CLOUD_'], viewPermission: 'CLOUD_VIEW', path: '/tenant/cloud' },
  { key: 'compute', label: 'Compute Management', prefixes: ['COMPUTE_'], viewPermission: 'COMPUTE_VIEW', path: '/tenant/compute' },
  { key: 'ticket', label: 'Ticket Management', prefixes: ['TICKET_'], viewPermission: 'TICKET_VIEW', path: '/tickets' },
  { key: 'organization', label: 'Organization Management', prefixes: ['ORGANIZATION_'], viewPermission: 'ORGANIZATION_VIEW', path: '/tenant/organizations' },
  { key: 'auditLog', label: 'Audit Log', prefixes: ['AUDIT_LOG_'], viewPermission: 'AUDIT_LOG_VIEW', path: '/tenant/audit-logs' },
  { key: 'reports', label: 'Reports', prefixes: ['REPORT_'], viewPermission: 'REPORT_VIEW', path: '/tenant/reports' },
];

export const getAccessibleModules = (permissions: string[]): ModuleConfig[] => {
  const permSet = new Set(permissions);
  return MODULE_CONFIGS.filter((m) => permSet.has(m.viewPermission));
};

export const getModuleForPermission = (permissionCode: string): ModuleConfig | undefined => {
  return MODULE_CONFIGS.find((m) => m.prefixes.some((p) => permissionCode.startsWith(p)));
};

// ---------------------------------------------------------------------
// Non-hook checks — safe to call inside event handlers / async functions
// where React hooks cannot be used (e.g. before firing an API call)
// ---------------------------------------------------------------------
export const getCurrentPermissions = (): string[] => {
  return store.getState().auth.user?.permissions || [];
};

export const hasPermission = (code: string): boolean => {
  return getCurrentPermissions().includes(code);
};

export const hasAnyPermission = (codes: string[]): boolean => {
  const perms = getCurrentPermissions();
  return codes.some((c) => perms.includes(c));
};

// Check a single permission
export const useHasPermission = (permission: string): boolean => {
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions || []);
  return permissions.includes(permission);
};

// Check if user has ANY of the given permissions
export const useHasAnyPermission = (permissionList: string[]): boolean => {
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions || []);
  return permissionList.some((p) => permissions.includes(p));
};

// Check if user has ALL of the given permissions
export const useHasAllPermissions = (permissionList: string[]): boolean => {
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions || []);
  return permissionList.every((p) => permissions.includes(p));
};

// Get raw permissions array
export const useUserPermissions = (): string[] => {
  return useSelector((state: RootState) => state.auth.user?.permissions || []);
};

// Get modules the current user can see (used by Sidebar + route guards)
export const useAccessibleModules = (): ModuleConfig[] => {
  const permissions = useUserPermissions();
  return getAccessibleModules(permissions);
};